import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/utils';

/**
 * Thin shadcn-style wrappers over @radix-ui/react-dialog.
 *
 * Why this exists: the hand-rolled `fixed inset-0` modals it replaces had
 * no accessibility behaviour at all — no Escape handling, no focus trap,
 * no focus restore on close, no `role="dialog"`/`aria-modal`, so Tab fell
 * straight through to the page behind and screen readers never announced
 * the dialog. Radix supplies all of that, plus scroll-locking the body
 * while open and `aria-labelledby` wiring when a `DialogTitle` is present.
 *
 * Visuals are intentionally unchanged: the same concrete Tailwind classes
 * the old overlays used (`bg-black bg-opacity-50`, `bg-white rounded-2xl
 * shadow-2xl`) are baked in as defaults and can be overridden per call
 * site via `className` / `overlayClassName` (merged with `cn()`).
 *
 * Radix logs a console warning when DialogContent lacks a DialogTitle —
 * every caller must render one (a `sr-only` title is fine when no visible
 * heading fits).
 */

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-black bg-opacity-50 z-50', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef(({ className, overlayClassName, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-2rem)] bg-white rounded-2xl shadow-2xl',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }) => (
  <div className={cn('flex justify-end space-x-3', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-bold text-gray-900', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-gray-500', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
